import type { CreateCanvas, FrameSize } from './types';

export type ContactSheetLayout = {
  columns: number;
  rows: number;
  /** Frame scale inside a cell, at most 1. */
  scale: number;
  cellWidth: number;
  cellHeight: number;
  labelHeight: number;
  gap: number;
  width: number;
  height: number;
};

const MAX_COLUMNS = 4;
const GAP_PX = 8;
const LABEL_PX = 20;

export function contactSheetLayout(
  frameCount: number,
  frame: FrameSize,
  maxEdgePx: number,
): ContactSheetLayout {
  const columns = Math.max(1, Math.min(MAX_COLUMNS, frameCount));
  const rows = Math.max(1, Math.ceil(frameCount / columns));
  const frameAreaWidth = maxEdgePx - (columns + 1) * GAP_PX;
  const frameAreaHeight = maxEdgePx - (rows + 1) * GAP_PX - rows * LABEL_PX;
  const scale = Math.min(
    1,
    frameAreaWidth / (columns * frame.width),
    frameAreaHeight / (rows * frame.height),
  );
  const cellWidth = Math.max(1, Math.floor(frame.width * scale));
  const cellHeight = Math.max(1, Math.floor(frame.height * scale));
  return {
    columns,
    rows,
    scale,
    cellWidth,
    cellHeight,
    labelHeight: LABEL_PX,
    gap: GAP_PX,
    width: columns * cellWidth + (columns + 1) * GAP_PX,
    height: rows * (cellHeight + LABEL_PX) + (rows + 1) * GAP_PX,
  };
}

export function formatTimestampLabel(timeSeconds: number): string {
  return `${timeSeconds.toFixed(2)}s`;
}

export function composeContactSheet(
  frames: ReadonlyArray<{ image: CanvasImageSource; timeSeconds: number }>,
  layout: ContactSheetLayout,
  createCanvas: CreateCanvas,
): HTMLCanvasElement {
  const sheet = createCanvas(layout.width, layout.height);
  const context = sheet.getContext('2d');
  if (!context) {
    throw new Error('A 2D canvas is not available for the contact sheet.');
  }
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, layout.width, layout.height);
  context.font = '12px sans-serif';
  context.textBaseline = 'middle';

  frames.forEach((frame, index) => {
    const column = index % layout.columns;
    const row = Math.floor(index / layout.columns);
    const x = layout.gap + column * (layout.cellWidth + layout.gap);
    const y = layout.gap + row * (layout.cellHeight + layout.labelHeight + layout.gap);
    context.drawImage(frame.image, x, y, layout.cellWidth, layout.cellHeight);
    context.strokeStyle = '#d0d5dd';
    context.strokeRect(x + 0.5, y + 0.5, layout.cellWidth - 1, layout.cellHeight - 1);
    context.fillStyle = '#344054';
    context.fillText(
      formatTimestampLabel(frame.timeSeconds),
      x,
      y + layout.cellHeight + layout.labelHeight / 2,
    );
  });
  return sheet;
}
